import React, { useCallback, useEffect, useState } from 'react';
import { List, QRCode, message, notification, Spin, Empty } from 'antd';
import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { CopyOutlined, DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { useTranslation } from 'react-i18next';
import { getRequest, patchRequest, postRequest } from 'api/apiClient';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { hasPermission } from 'utils/utils';

// Use environment-specific control URL for Self Sign-In
const controlUrl = process.env.REACT_APP_CONTROL_URL || 'https://control.invisit.us';

interface SelfSignInFormProps {
    selectedLocationId?: number;
    onRevokeSuccess?: () => void;
    form?: any;
}

const SelfSignInForm: React.FC<SelfSignInFormProps> = ({ selectedLocationId, onRevokeSuccess, form }) => {
    const { t } = useTranslation();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const confirmModal = useConfirmModal();

    const hasLocationWrite = hasPermission(tokenScopeList, orgId, 'o', 'location:w');

    const [selfSignInUrl, setSelfSignInUrl] = useState<string>('');
    const [qrCodeValue, setQrCodeValue] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [selfSignTokenId, setSelfSignTokenId] = useState<number | null>(null);

    // Fetch self sign-in token and workflow settings
    const fetchSelfSignData = useCallback(async () => {
        if (!selectedLocationId || !orgId) {
            return;
        }

        setLoading(true);

        try {
            // Fetch token
            const response = await getRequest(`/orgs/${orgId}/sites/${selectedLocationId}/selfSignToken`);
            const tokenData = response.data?.[0];

            // If data array has token, show content; if empty, show generate button
            if (tokenData?.locationToken) {
                const url = `${controlUrl}/selfSignIn?${tokenData.locationToken}`;
                setSelfSignInUrl(url);
                setQrCodeValue(url);
                setSelfSignTokenId(tokenData.id);
            } else {
                // No token available (empty data array)
                setSelfSignInUrl('');
                setQrCodeValue('');
                setSelfSignTokenId(null);
            }
        } catch (err: any) {
            console.error('Error fetching self sign-in data:', err);
            setSelfSignInUrl('');
            setQrCodeValue('');
            setSelfSignTokenId(null);
        } finally {
            setLoading(false);
        }
    }, [selectedLocationId, orgId]);

    useEffect(() => {
        fetchSelfSignData();
    }, [fetchSelfSignData]);

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(selfSignInUrl).then(() => {
            message.success(t('location.selfSignIn.urlCopied') || 'URL copied to clipboard');
        }).catch((error) => {
            message.error('Failed to copy URL');
            console.error('Copy failed', error);
        });
    };

    const handleDownloadQrCode = () => {
        const canvas = document.getElementById('selfSignInQrCode')?.querySelector<HTMLCanvasElement>('canvas');
        if (canvas) {
            const url = canvas.toDataURL();
            const a = document.createElement('a');
            a.download = `self-signin-qr-${selectedLocationId}.png`;
            a.href = url;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            message.success(t('location.selfSignIn.qrCodeDownloaded') || 'QR code downloaded successfully');
        }
    };

    // Generate Self Sign-In URL with confirmation
    const handleGenerateClick = () => {
        confirmModal({
            title: t('location.selfSignIn.generateWarningTitle') || 'Generate Self Sign-In URL',
            content: t('location.selfSignIn.generateWarningMessage') || 'Are you sure you want to generate a Self Sign-In URL?',
            okText: t('Yes') || 'Yes',
            cancelText: t('No') || 'No',
            onOk: async () => {
                await handleGenerateUrl();
            }
        });
    };

    const handleGenerateUrl = async () => {
        if (!selectedLocationId || !orgId) return;

        setIsGenerating(true);
        try {
            // 1. Enable Self Sign In and Self Sign Out
            await patchRequest(`/orgs/${orgId}/sites/${selectedLocationId}/workflows`, {
                SelfSignIn: 1,
                SelfSignOut: 1
            });

            // 2. Update form values to reflect the enabled state
            if (form) {
                form.setFieldsValue({
                    SelfSignIn: true,
                    SelfSignOut: true
                });
            }

            // 3. Create self sign token
            await postRequest(`/orgs/${orgId}/sites/${selectedLocationId}/selfSignToken`, {});

            // 4. Fetch the created token
            await fetchSelfSignData();

            notification.success({
                message: t('notification.success'),
                description: t('location.selfSignIn.generateSuccess') || 'Self Sign-In URL generated successfully',
                placement: 'bottomRight',
            });
        } catch (err: any) {
            console.error('Error generating self sign-in URL:', err);
            notification.error({
                message: t('notification.error'),
                description: t('location.selfSignIn.generateError') || 'Failed to generate Self Sign-In URL',
                placement: 'bottomRight',
            });
        } finally {
            setIsGenerating(false);
        }
    };

    // Revoke Self Sign-In URL
    const handleRevokeClick = () => {
        confirmModal({
            title: t('location.selfSignIn.revokeWarningTitle') || 'Revoke Self Sign-In URL',
            content: t('location.selfSignIn.revokeWarningMessage') || 'Are you sure you want to revoke this URL?',
            okText: t('Yes') || 'Yes',
            cancelText: t('No') || 'No',
            onOk: async () => {
                if (!selfSignTokenId || !orgId || !selectedLocationId) return;

                try {
                    // 1. Revoke the token
                    await patchRequest(`/orgs/${orgId}/sites/${selectedLocationId}/selfSignToken/${selfSignTokenId}`, {
                        status: 0
                    });

                    // 2. Disable Self Sign In and Self Sign Out
                    await patchRequest(`/orgs/${orgId}/sites/${selectedLocationId}/workflows`, {
                        SelfSignIn: 0,
                        SelfSignOut: 0
                    });

                    // Clear local state
                    setSelfSignInUrl('');
                    setQrCodeValue('');
                    setSelfSignTokenId(null);

                    // Notify parent component to turn off switches
                    if (onRevokeSuccess) {
                        onRevokeSuccess();
                    }

                    notification.success({
                        message: t('notification.success'),
                        description: t('location.selfSignIn.revokeSuccess') || 'Self Sign-In URL has been revoked successfully',
                        placement: 'bottomRight',
                    });
                } catch (err: any) {
                    console.error('Error revoking self sign token:', err);
                    notification.error({
                        message: t('notification.error'),
                        description: t('location.selfSignIn.revokeError') || 'Failed to revoke Self Sign-In URL',
                        placement: 'bottomRight',
                    });
                }
            }
        });
    };

    return (
        <>
            {(loading || isGenerating) ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <Spin indicator={<LoadingOutlined spin />} />
                </div>
            ) : (
                <>
                    {!selfSignInUrl ? (
                        // No URL - Show Generate Button or Empty state
                        <>
                            {hasLocationWrite ? (
                                <OpButton
                                    type="primary"
                                    ghost
                                    style={{ width: '100%' }}
                                    onClick={handleGenerateClick}
                                >
                                    {t('location.selfSignIn.generateUrl') || 'Generate Self Sign-In URL'}
                                </OpButton>
                            ) : (
                                <Empty description={t('location.selfSignIn.empty') || 'No Self Sign-In URL generated'} />
                            )}
                        </>
                    ) : (
                        // URL exists - Show toggles, URL and QR Code
                        <>
                            {/* Self Sign In/Out Toggles */}
                            <List
                                header={t('location.selfSignIn.title') || 'Self Sign In'}
                                dataSource={[
                                    { name: 'SelfSignIn', label: t('location.selfSignIn.allowSelfSignIn') || 'Allow Self Sign In' },
                                    { name: 'SelfSignOut', label: t('location.selfSignIn.allowSelfSignOut') || 'Allow Self Sign Out' },
                                ]}
                                bordered
                                renderItem={item => (
                                    <List.Item>
                                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <OpForm.Switch
                                                name={item.name}
                                                style={{ marginBottom: 0 }}
                                            />
                                            <span style={{ marginLeft: 8 }}>{item.label}</span>
                                        </div>
                                    </List.Item>
                                )}
                            />

                            {/* URL and QR Code */}
                            <List
                                header={t('location.selfSignIn.urlSection') || 'Self Sign-In URL & QR Code'}
                                bordered
                                style={{ marginTop: '24px' }}
                            >
                                <List.Item>
                                    <List.Item.Meta
                                        description={
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                flexWrap: 'wrap',
                                                marginTop: '8px'
                                            }}>
                                                <a
                                                    href={selfSignInUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        flex: 1,
                                                        minWidth: '300px',
                                                        wordBreak: 'break-all',
                                                        color: '#1890ff'
                                                    }}
                                                >
                                                    {selfSignInUrl}
                                                </a>
                                                <OpButton
                                                    type="default"
                                                    icon={<CopyOutlined />}
                                                    onClick={handleCopyUrl}
                                                />
                                            </div>
                                        }
                                    />
                                </List.Item>
                                <List.Item>
                                    <List.Item.Meta
                                        description={
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                width: '100%',
                                                gap: '16px',
                                                marginTop: '16px'
                                            }}>
                                                <OpSpace id="selfSignInQrCode" direction="vertical" align="center">
                                                    <QRCode
                                                        value={qrCodeValue}
                                                        size={200}
                                                        type="canvas"
                                                        color="#000000"
                                                        bgColor="#ffffff"
                                                    />
                                                </OpSpace>
                                                <OpButton
                                                    type="default"
                                                    icon={<DownloadOutlined />}
                                                    onClick={handleDownloadQrCode}
                                                />
                                            </div>
                                        }
                                    />
                                </List.Item>
                            </List>

                            {/* Revoke Button (only show if user has write permission) */}
                            {hasLocationWrite && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                                    <OpButton
                                        type="primary"
                                        danger
                                        onClick={handleRevokeClick}
                                    >
                                        {t('location.selfSignIn.revokeUrl') || 'Revoke URL'}
                                    </OpButton>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default SelfSignInForm;
